import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import taskService, { Task, Tagesansicht } from '../services/taskService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, useFocusParam } from '../components/ui';
import { Icon, IconName } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { faelligkeit, fehlerText } from '../vertrieb/format';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/**
 * Der Arbeitstag.
 *
 * Vier Faecher statt einer Liste: ueberfaellig und heute verlangen andere
 * Reaktionen, und eine gemeinsame Liste laesst beides gleich aussehen.
 * Jede Aufgabe verlinkt dorthin, wo sie herkommt: Firmenakte, Vorgang in
 * der Pipeline, Antwort im Postfach.
 */

const ART: Record<string, { label: string; icon: IconName }> = {
  todo: { label: 'Aufgabe', icon: 'check' },
  call: { label: 'Anruf', icon: 'megaphone' },
  email: { label: 'E-Mail', icon: 'mail' },
  meeting: { label: 'Termin', icon: 'calendar' },
};

const heuteIso = () => new Date().toISOString().slice(0, 10);

const Zeile: React.FC<{
  aufgabe: Task;
  onErledigt: (t: Task) => void;
  onVerschieben: (t: Task, tage: number) => void;
  onLoeschen: (t: Task) => void;
  /** Ohne vertrieb.bearbeiten (Welle 2): Haken gesperrt, Aktionen ausgeblendet. */
  darfBearbeiten: boolean;
}> = ({ aufgabe: t, onErledigt, onVerschieben, onLoeschen, darfBearbeiten }) => {
  const art = ART[t.kind] ?? { label: t.kind, icon: 'check' as IconName };
  const f = faelligkeit(t.dueAt);
  const leadName = t.lead ? ([t.lead.firstName, t.lead.lastName].filter(Boolean).join(' ') || t.lead.email) : null;

  return (
    <li data-focus-id={t.id} className={`aufgabe ${f?.ueberfaellig ? 'aufgabe-ueberfaellig' : ''} prio-${t.priority}`}>
      <input
        type="checkbox"
        checked={t.status === 'done'}
        disabled={!darfBearbeiten}
        onChange={() => onErledigt(t)}
        aria-label={`${t.title} erledigt`}
      />
      <div className="aufgabe-inhalt">
        <div className="aufgabe-kopf">
          <StatusBadge tone="neutral" icon={art.icon}>{art.label}</StatusBadge>
          {t.priority === 'high' && <StatusBadge tone="danger">hoch</StatusBadge>}
          <strong>{t.title}</strong>
          {f && <span className={`aufgabe-termin${f.ueberfaellig ? ' text-error' : ''}`}>{f.text}</span>}
        </div>
        {(t.company || t.deal || t.activity || leadName || t.assignee) && (
          <div className="aufgabe-bezug">
            {t.company && <Link to={`/companies/${t.company.id}`}><Icon name="building" size={12} /> {t.company.name}</Link>}
            {t.deal && <> · <Link to={`/pipeline?deal=${t.deal.id}`}><Icon name="kanban" size={12} /> {t.deal.title}</Link></>}
            {t.activity && <> · <Link to={`/outreach-inbox?eintrag=${t.activity.id}`}><Icon name="inbox" size={12} /> Antwort{t.activity.campaignName ? ` aus „${t.activity.campaignName}“` : ''}</Link></>}
            {leadName && t.lead && <> · <Link to={`/lead-sync?lead=${encodeURIComponent(t.lead.id)}`} title="Lead öffnen"><Icon name="user" size={12} /> {leadName}</Link></>}
            {t.assignee && <> · {t.assignee.name}</>}
          </div>
        )}
        {t.description && <div className="aufgabe-text">{t.description}</div>}
      </div>
      {darfBearbeiten && (
        <div className="aufgabe-aktionen">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onVerschieben(t, 1)} title="auf morgen verschieben">+1 T</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onVerschieben(t, 7)} title="um eine Woche verschieben">+7 T</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onLoeschen(t)} title="Aufgabe löschen" aria-label={`${t.title} löschen`}>
            <Icon name="trash" size={14} />
          </button>
        </div>
      )}
    </li>
  );
};

const TasksPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  // Welle 2: jede Aenderung an Aufgaben verlangt vertrieb.bearbeiten (Buchhaltung liest nur).
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const [ansicht, setAnsicht] = useState<Tagesansicht | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [neuTitel, setNeuTitel] = useState('');
  const [neuArt, setNeuArt] = useState('todo');
  const [neuTermin, setNeuTermin] = useState(heuteIso());

  const laden = useCallback(async () => {
    try {
      setAnsicht((await taskService.heute()) ?? null);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // ?aufgabe=<id>: Sprung aus Vorgang, Cockpit & Co. Die Tagesansicht zeigt
  // nur Offenes -- eine erledigte Aufgabe gibt es hier nicht zu sehen.
  const aufgabeId = useFocusParam('aufgabe', !laedt);
  const aufgabeSichtbar = !aufgabeId || !ansicht
    || [...ansicht.ueberfaellig, ...ansicht.heute, ...ansicht.woche, ...ansicht.ohneTermin].some((t) => t.id === aufgabeId);

  const erledigt = async (t: Task) => {
    try {
      await taskService.erledigen(t.id, t.status !== 'done');
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Abhaken fehlgeschlagen'));
    }
  };

  const verschieben = async (t: Task, tage: number) => {
    const basis = t.dueAt ? new Date(t.dueAt) : new Date();
    basis.setDate(basis.getDate() + tage);
    try {
      await taskService.update(t.id, { dueAt: basis.toISOString() });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Verschieben fehlgeschlagen'));
    }
  };

  const loeschen = async (t: Task) => {
    const ok = await confirm({ title: 'Aufgabe löschen?', message: t.title, confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;
    try {
      await taskService.remove(t.id);
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const anlegen = async () => {
    if (!neuTitel.trim()) return;
    try {
      await taskService.create({
        title: neuTitel.trim(),
        kind: neuArt,
        dueAt: neuTermin ? new Date(`${neuTermin}T09:00:00`).toISOString() : null,
      });
      setNeuTitel('');
      push({ message: 'Aufgabe angelegt.', severity: 'success' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Anlegen fehlgeschlagen'));
    }
  };

  const ausAntworten = async () => {
    try {
      const b = await taskService.ausAntworten();
      push({
        message: b.neu
          ? `${b.neu} Aufgabe(n) aus liegengebliebenen Antworten angelegt (${b.schonDa} gab es schon).`
          : `Keine neuen – ${b.schonDa} Antworten haben bereits eine Aufgabe.`,
        severity: b.neu ? 'success' : 'info',
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Nachtrag fehlgeschlagen'));
    }
  };

  const fach = (titel: string, liste: Task[], hinweis?: string) => {
    if (!liste.length) return null;
    return (
      <section className="aufgaben-fach" aria-label={titel}>
        <h2>{titel} <span className="fach-zahl">{liste.length}</span></h2>
        {hinweis && <p className="inbox-item-hinweis">{hinweis}</p>}
        <ul className="aufgaben-liste">
          {liste.map((t) => (
            <Zeile key={t.id} aufgabe={t} onErledigt={erledigt} onVerschieben={verschieben} onLoeschen={loeschen} darfBearbeiten={darfBearbeiten} />
          ))}
        </ul>
      </section>
    );
  };

  const leer = ansicht
    && !ansicht.ueberfaellig.length && !ansicht.heute.length
    && !ansicht.woche.length && !ansicht.ohneTermin.length;

  return (
    <div className="page-container">
      <SalesProcessBar label="Heute" />
      <PageHeader
        title="Heute"
        subtitle="Was ansteht – über Firmen, Vorgänge und Antworten hinweg."
        actions={darfBearbeiten ? (
          <button type="button" className="btn btn-secondary" onClick={ausAntworten}>
            <Icon name="inbox" size={14} /> Aus Antworten nachtragen
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Aufgaben anlegen, abhaken und verschieben" />

      {fehler && <div className="alert alert-error">{fehler}</div>}
      {!laedt && !aufgabeSichtbar && (
        <div className="alert alert-info" role="status">Die verlinkte Aufgabe ist erledigt oder gelöscht und steht deshalb nicht in der Tagesansicht.</div>
      )}

      {ansicht && (
        <KpiRow
          aria-label="Aufgaben-Kennzahlen"
          items={[
            { label: 'überfällig', value: ansicht.summe.ueberfaellig, tone: ansicht.summe.ueberfaellig ? 'warn' : 'neutral' },
            { label: 'heute fällig', value: ansicht.summe.heute },
            { label: 'offen gesamt', value: ansicht.summe.offen },
          ]}
        />
      )}

      {darfBearbeiten && (
        <div className="quick-form" role="form" aria-label="Neue Aufgabe">
          <label>
            Neue Aufgabe
            <input
              type="text"
              placeholder="Was ist zu tun?"
              value={neuTitel}
              onChange={(e) => setNeuTitel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }}
            />
          </label>
          <label>
            Art
            <select value={neuArt} onChange={(e) => setNeuArt(e.target.value)}>
              <option value="todo">Aufgabe</option>
              <option value="call">Anruf</option>
              <option value="email">E-Mail</option>
              <option value="meeting">Termin</option>
            </select>
          </label>
          <label>
            Fällig am
            <input type="date" value={neuTermin} onChange={(e) => setNeuTermin(e.target.value)} />
          </label>
          <div className="quick-form__actions">
            <button type="button" className="btn btn-primary" onClick={anlegen} disabled={!neuTitel.trim()}>Anlegen</button>
          </div>
        </div>
      )}

      {laedt && !ansicht && <LoadingState rows={4} label="Aufgaben werden geladen" />}

      {leer && (
        <EmptyState
          icon="sun"
          title="Nichts offen"
          description={darfBearbeiten
            ? '„Aus Antworten nachtragen“ holt liegengebliebene Antworten aus dem Postfach auf diese Liste. Aufgaben zu Firmen und Vorgängen legst du direkt in der Akte oder im Vorgang an.'
            : 'Gerade steht keine offene Aufgabe an.'}
          action={darfBearbeiten ? { label: 'Aus Antworten nachtragen', onClick: ausAntworten } : undefined}
          secondaryAction={{ label: 'Zum Cockpit', to: '/vertrieb' }}
        />
      )}

      {ansicht && (
        <>
          {fach('Überfällig', ansicht.ueberfaellig)}
          {fach('Heute', ansicht.heute)}
          {fach('Diese Woche', ansicht.woche)}
          {fach('Ohne Termin', ansicht.ohneTermin, 'Ohne Termin wird nichts erinnert – entweder einen setzen oder abhaken.')}
        </>
      )}
    </div>
  );
};

export default TasksPage;
